import type { FormEvent, MouseEvent } from "react";
import { useState } from "react";
import type { User } from "../api";
import { api } from "../api";

type Props = {
  token: string;
  user: User;
  onClose: () => void;
  onSaved?: () => void;
};

export function RenameUserModal({ token, user, onClose, onSaved }: Props) {
  const [name, setName] = useState(user.username);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onBackdrop = (ev: MouseEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget && !busy) onClose();
  };

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    const next = name.trim();
    if (next.length < 2) {
      setError("Username must be at least 2 characters.");
      return;
    }
    if (next === user.username) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await api.patchUser(token, user.id, { username: next });
      onSaved?.();
      onClose();
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e && "message" in e ? String((e as { message?: string }).message) : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onBackdrop}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-md w-full flex flex-col"
        role="dialog"
        aria-labelledby="rename-user-modal-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 id="rename-user-modal-title" className="text-base font-semibold text-slate-900">
              Rename user
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              id <span className="font-mono">{user.id}</span> · current{" "}
              <span className="font-mono">{user.username}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn shrink-0" disabled={busy} aria-label="Close">
            Close
          </button>
        </div>

        <form className="p-4 space-y-3" onSubmit={(ev) => void onSubmit(ev)}>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">New username</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={128}
              autoFocus
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Their saved JWTs continue to work (the token's <code>sub</code> claim is the user id, not the
              username). New logins use the new username.
            </p>
          </div>

          <div className="pt-2 flex justify-end gap-2 border-t border-slate-100">
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
